import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react';
import './ShowMeWhere.css';

export interface ShowMeWhereGuideState {
  x: number;
  y: number;
  dx: number;
  dy: number;
  nonce: number;
}

interface UseShowMeWhereOptions {
  flashDurationMs?: number;
  guideDurationMs?: number;
}

export const useShowMeWhere = <S extends HTMLElement, T extends HTMLElement>(
  sourceRef: RefObject<S | null>,
  targetRef: RefObject<T | null>,
  { flashDurationMs = 2000, guideDurationMs = 1700 }: UseShowMeWhereOptions = {},
) => {
  const [isFlashing, setIsFlashing] = useState(false);
  const [guide, setGuide] = useState<ShowMeWhereGuideState | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const guideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      if (guideTimerRef.current) clearTimeout(guideTimerRef.current);
    };
  }, []);

  const trigger = useCallback(() => {
    setIsFlashing(true);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => {
      setIsFlashing(false);
      flashTimerRef.current = null;
    }, flashDurationMs);

    if (guideTimerRef.current) {
      clearTimeout(guideTimerRef.current);
      guideTimerRef.current = null;
    }

    const fromEl = sourceRef.current;
    const toEl = targetRef.current;
    if (!fromEl || !toEl) return;

    const fromRect = fromEl.getBoundingClientRect();
    const toRect = toEl.getBoundingClientRect();
    const startX = fromRect.left + fromRect.width / 2;
    const startY = fromRect.top + fromRect.height / 2;
    const endX = toRect.left + toRect.width / 2;
    const endY = toRect.top + toRect.height / 2;

    setGuide({
      x: startX,
      y: startY,
      dx: endX - startX,
      dy: endY - startY,
      nonce: Date.now(),
    });
    guideTimerRef.current = setTimeout(() => {
      setGuide(null);
      guideTimerRef.current = null;
    }, guideDurationMs);
  }, [flashDurationMs, guideDurationMs, sourceRef, targetRef]);

  const flashClassName = isFlashing ? 'showMeWhereFlash' : '';

  return useMemo(() => ({ trigger, guide, isFlashing, flashClassName }), [trigger, guide, isFlashing, flashClassName]);
};

interface ShowMeWhereGuideProps {
  guide: ShowMeWhereGuideState | null;
  className?: string;
}

export const ShowMeWhereGuide: React.FC<ShowMeWhereGuideProps> = ({ guide, className = '' }) => {
  if (!guide) return null;

  const style: CSSProperties & Record<'--guide-dx' | '--guide-dy', string> = {
    left: guide.x,
    top: guide.y,
    '--guide-dx': `${guide.dx}px`,
    '--guide-dy': `${guide.dy}px`,
  };

  return (
    <div key={guide.nonce} aria-hidden="true">
      {/* main orb with core glow + halo */}
      <div className={`showMeWhereGuide ${className}`.trim()} style={style} />
      {/* trailing wisps — two layers for visible fluid tail */}
      <div className="showMeWhereTrail" style={style} />
      <div className="showMeWhereTrail" style={style} />
      {/* sparkle particles along the path */}
      <div className="showMeWhereSpark" style={style} />
      <div className="showMeWhereSpark" style={style} />
      <div className="showMeWhereSpark" style={style} />
      <div className="showMeWhereSpark" style={style} />
    </div>
  );
};
